import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const DAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag'];
const TIMES = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00'];

const SchedulePage = () => {
  const { getAuthHeader } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [schedule, setSchedule] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const [subRes, schRes] = await Promise.all([
        api.getSubjects(headers),
        api.getSchedule(headers)
      ]);
      setSubjects(subRes.data);
      
      const scheduleMap = {};
      schRes.data.forEach(entry => {
        const key = `${entry.day}-${entry.time}`;
        scheduleMap[key] = {
          subject_id: entry.subject_id,
          subject_name: entry.subject_name
        };
      });
      setSchedule(scheduleMap);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleCellChange = (day, time, subjectId) => {
    const key = `${day}-${time}`;
    if (subjectId === 'none') {
      // Remove entry
      const newSchedule = { ...schedule };
      delete newSchedule[key];
      setSchedule(newSchedule);
      return;
    }
    const subject = subjects.find(s => s.id === subjectId);
    setSchedule(prev => ({
      ...prev,
      [key]: { subject_id: subjectId, subject_name: subject?.name || '' }
    }));
  };

  const handleSave = async () => {
    try {
      const headers = getAuthHeader();
      const entries = Object.entries(schedule)
        .filter(([_, value]) => value && value.subject_id)
        .map(([key, value]) => {
          const [day, time] = key.split('-');
          return { day, time, subject_id: value.subject_id, subject_name: value.subject_name };
        });
      
      await api.saveSchedule({ entries }, headers);
      toast.success('Stundenplan gespeichert!');
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold text-slate-900 mb-2">Wochenplan</h1>
          <p className="text-lg text-slate-600">Trage deinen Stundenplan ein</p>
        </div>
        <Button onClick={handleSave} data-testid="save-schedule-button" className="rounded-full bg-primary-600 hover:bg-primary-700 text-white font-bold px-6">
          <Save size={20} className="mr-2" />
          Speichern
        </Button>
      </div>

      <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className="p-3 text-left font-heading font-bold text-slate-900">Zeit</th>
              {DAYS.map(day => (
                <th key={day} className="p-3 text-center font-heading font-bold text-slate-900">{day}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TIMES.map(time => (
              <tr key={time} className="border-t border-slate-100">
                <td className="p-3 font-medium text-slate-600">{time}</td>
                {DAYS.map(day => {
                  const key = `${day}-${time}`;
                  const cell = schedule[key];
                  const subject = cell ? subjects.find(s => s.id === cell.subject_id) : null;
                  return (
                    <td key={`${day}-${time}`} className="p-2">
                      <Select
                        value={cell?.subject_id || 'none'}
                        onValueChange={(value) => handleCellChange(day, time, value)}
                      >
                        <SelectTrigger
                          data-testid={`schedule-${day}-${time}`}
                          className="h-12 rounded-xl border-2"
                          style={subject ? { backgroundColor: subject.color + '20', borderColor: subject.color + '40' } : {}}
                        >
                          <SelectValue placeholder="Frei">
                            {subject ? `${subject.icon} ${subject.name}` : 'Frei'}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Frei</SelectItem>
                          {subjects.map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.icon} {s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SchedulePage;
